import Phaser from 'phaser';
import { GAME } from '../constants';
import { StoryBeat } from '../story/Lore';
import { FONT_FAMILY, createPixelPanel, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut } from '../ui/SceneTransitions';
import { createAmbientMotes } from '../ui/ParticleEffects';

interface StorySceneData {
  beat: StoryBeat;
  next: string;
  nextData?: Record<string, unknown>;
}

// Generic narrative interstitial. Shows a title + body + continue prompt, then
// hands off to the next scene. Used for the prologue, act transitions, and the
// ending so the run has a beginning, a middle, and a payoff.
export class StoryScene extends Phaser.Scene {
  private advanced = false;
  private continueBtn?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'StoryScene' });
  }

  create(data: StorySceneData): void {
    this.advanced = false;
    fadeIn(this, 450);

    const cx = GAME.WIDTH / 2;
    const beat = data.beat;
    const accent = beat.accent ?? 0xffd166;

    drawPixelBackdrop(this);
    createAmbientMotes(this, 10);

    const panel = createPixelPanel(this, cx, GAME.HEIGHT / 2, GAME.WIDTH - 220, GAME.HEIGHT - 200, {
      fill: 0x120f1f,
      stroke: 0x6a4a78,
      accent,
      alpha: 0.95,
    });
    panel.elements.forEach(el => el.setDepth(1));

    const titleY = 170;

    // Title with a soft drop shadow for that retro marquee feel.
    this.add.text(cx + 2, titleY + 2, beat.title, {
      fontSize: '26px',
      fontFamily: FONT_FAMILY,
      color: '#20131f',
      fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5).setDepth(2);
    const title = this.add.text(cx, titleY, beat.title, {
      fontSize: '26px',
      fontFamily: FONT_FAMILY,
      color: '#ffe6a8',
      fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5).setDepth(3).setAlpha(0);

    this.tweens.add({ targets: title, alpha: 1, duration: 600, ease: 'Sine.easeIn' });

    // Thin accent rule under the title. Grown with scaleX (reliable for a
    // Rectangle) and sized to stay inside the panel.
    const rule = this.add.rectangle(cx, titleY + 28, GAME.WIDTH - 420, 2, accent, 0.8)
      .setDepth(3).setScale(0, 1);
    this.tweens.add({ targets: rule, scaleX: 1, duration: 700, ease: 'Cubic.easeOut' });

    const body = this.add.text(cx, GAME.HEIGHT / 2 + 6, beat.body, {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#dfe2f5',
      align: 'center',
      lineSpacing: 10,
      wordWrap: { width: GAME.WIDTH - 320 },
    }).setOrigin(0.5).setDepth(3).setAlpha(0);

    this.tweens.add({ targets: body, alpha: 1, duration: 900, delay: 350, ease: 'Sine.easeIn' });

    // Continue button appears after the body has had a beat to land.
    this.continueBtn = this.add.text(cx, GAME.HEIGHT - 132, beat.continueLabel, {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#fff6dc',
      backgroundColor: '#2c2140',
      padding: { x: 22, y: 12 },
      align: 'center',
    }).setOrigin(0.5).setDepth(4).setAlpha(0);
    this.continueBtn.setStroke('#0c0a16', 4);

    this.tweens.add({
      targets: this.continueBtn,
      alpha: 1,
      duration: 500,
      delay: 1100,
      ease: 'Sine.easeIn',
      onComplete: () => {
        if (!this.continueBtn) return;
        this.continueBtn.setInteractive({ useHandCursor: true });
        this.continueBtn.on('pointerover', () => this.continueBtn?.setColor('#ffe9b5'));
        this.continueBtn.on('pointerout', () => this.continueBtn?.setColor('#fff6dc'));
        this.continueBtn.on('pointerdown', () => this.advance(data));
        this.tweens.add({
          targets: this.continueBtn,
          alpha: 0.78,
          duration: 900,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });

    const hint = this.add.text(cx, GAME.HEIGHT - 92, 'click  /  SPACE  /  ENTER', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#8f86b6',
    }).setOrigin(0.5).setDepth(4);
    this.tweens.add({ targets: hint, alpha: 0.6, duration: 1000, delay: 1100, from: 0 });

    // Any of these advance. Keyboard works even before the button fades in,
    // so an impatient reader is never blocked.
    this.input.keyboard?.on('keydown-SPACE', () => this.advance(data));
    this.input.keyboard?.on('keydown-ENTER', () => this.advance(data));
    this.input.on('pointerdown', () => this.advance(data));
  }

  private advance(data: StorySceneData): void {
    if (this.advanced) return;
    this.advanced = true;
    fadeOut(this, 300).then(() => {
      this.scene.start(data.next, data.nextData ?? {});
    });
  }
}
